import { Auth0AuthClient } from './Auth0AuthClient';
import { MsalAuthClient } from './MsalAuthClient';

/** The signed-in account, independent of the identity provider. */
export interface AuthAccount {
  email: string;
}

/**
 * The identity provider behind the app (B2C via MSAL, or Auth0 during the parallel run). Chosen once
 * per page load (see {@link isAuth0Provider}); switching back to B2C needs no rebuild.
 */
export interface AuthClient {
  /** Completes a login redirect if this page load is one. Resolves true when a login just completed. */
  initialize(): Promise<boolean>;
  /** The cached account, or null when nobody is signed in. Synchronous: read on every request. */
  getAccount(): AuthAccount | null;
  /** An API access token; `forceRefresh` skips the cache. */
  getAccessToken(options?: { forceRefresh?: boolean }): Promise<string>;
  /** Full-page navigation to the identity provider's login. */
  loginRedirect(): Promise<void>;
  logout(): Promise<void>;
  /** Whether an error means the user has to sign in again (as opposed to a transient failure). */
  isInteractionRequired(error: unknown): boolean;
}

/** Where a tester's per-browser choice is remembered (only honoured when `AUTH0_OPT_IN` is on). */
const OPT_IN_STORAGE_KEY = 'klayo.authProvider';
const OPT_IN_QUERY_PARAM = 'auth';

let auth0Provider: boolean | null = null;
let client: AuthClient | null = null;

/**
 * Auth0 when `config.js` says `AUTH_PROVIDER: "auth0"` (the whole site), or - where `AUTH0_OPT_IN` is
 * on (dev) - when this browser opted in with `?auth=auth0` (`?auth=b2c` opts out). Without the flag the
 * query is ignored, so test/prod users cannot switch themselves. Decided once per page load: every
 * token read in this load must go to the same provider.
 */
export function isAuth0Provider(): boolean {
  auth0Provider ??= decideAuth0Provider();
  return auth0Provider;
}

function decideAuth0Provider(): boolean {
  const env = window.app?.env;
  if (env?.AUTH_PROVIDER === 'auth0') return true;
  if (String(env?.AUTH0_OPT_IN) !== 'true') return false;
  return resolveOptIn() === 'auth0';
}

/** Applies a `?auth=` switch (remembers it, removes it from the URL), else returns the remembered choice. */
function resolveOptIn(): string | null {
  const url = new URL(window.location.href);
  const requested = url.searchParams.get(OPT_IN_QUERY_PARAM);
  try {
    if (requested === 'auth0' || requested === 'b2c') {
      localStorage.setItem(OPT_IN_STORAGE_KEY, requested);
      // Drop it from the address bar so it isn't bookmarked or carried through the login round trip.
      url.searchParams.delete(OPT_IN_QUERY_PARAM);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      return requested;
    }
    return localStorage.getItem(OPT_IN_STORAGE_KEY);
  } catch {
    // Storage blocked (private mode): stay on B2C. Auth0's token cache needs storage too, and the
    // login callback could not remember the choice, so the round trip would never complete.
    return null;
  }
}

/** Test seam: the provider decision and client are per page load and would leak between cases. */
export function resetAuthClient(): void {
  auth0Provider = null;
  client = null;
}

/** Created on first use, not at module load: this module is reached while `src/index` is still loading. */
export function getAuthClient(): AuthClient {
  client ??= isAuth0Provider() ? new Auth0AuthClient(window.app.env) : new MsalAuthClient();
  return client;
}
